import React from "react";
import { Helmet } from "react-helmet";
import Configs from "../Assets/editable-stuff/configurations.json";

// Absolute origin used for canonical + Open Graph URLs. Crawlers/unfurlers
// (LinkedIn, WhatsApp, iMessage, X) require absolute URLs, not relative paths.
const SITE_URL = "https://www.needforgeeks.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const PHONE_NUMBER = `+1${Configs.PhoneNumber}`;

const defaultOrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NeedForGeeks",
  url: SITE_URL,
  logo: `${SITE_URL}/android-chrome-192x192.png`,
  email: Configs.EmailAddress,
  telephone: PHONE_NUMBER,
  sameAs: Configs.icons.map((icon) => icon.url),
};

const defaultLocalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "NeedForGeeks",
  url: SITE_URL,
  image: DEFAULT_IMAGE,
  telephone: PHONE_NUMBER,
  email: Configs.EmailAddress,
  address: {
    "@type": "PostalAddress",
    addressLocality: "New York",
    addressRegion: "NY",
    addressCountry: "US",
  },
  areaServed: [
    {
      "@type": "City",
      name: "New York",
    },
  ],
  sameAs: Configs.icons.map((icon) => icon.url),
};

// Per-page SEO + social share tags. Gives every route a unique <title>,
// meta description, canonical URL, and Open Graph / Twitter Card so a shared
// link renders a meaningful preview instead of a generic "NeedForGeeks" card.
const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  imageAlt = "NeedForGeeks tech services",
  type = "website",
  noindex = false,
  keywords,
  structuredData = [],
}) => {
  const url = `${SITE_URL}${path}`;
  const schemaList = Array.isArray(structuredData) ? structuredData : [structuredData];
  const mergedSchemas = [
    defaultOrganizationSchema,
    ...(!noindex ? [defaultLocalBusinessSchema] : []),
    ...schemaList.filter(Boolean),
  ];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />
      <meta name="author" content="NeedForGeeks" />
      <meta name="publisher" content="NeedForGeeks" />

      {/* Open Graph (Facebook, LinkedIn, WhatsApp, iMessage, Slack) */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="NeedForGeeks" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {mergedSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
